import {
  Box,
  Button,
  Dialog,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import xGrey from "../../../img/xGrey.png";
import { AdminManagementButton } from "../../AdminManagement/AdminManagementButton";
import { useContext, useEffect, useRef, useState } from "react";
import {
  Mahnung,
  Mahngebuhr,
  GebuhrTypes,
  MahnstufeEnum,
  MahnungStatus,
} from "../../../models/admin-management-entities";
import {
  getMahnung,
  resetMahnung,
  incrementMahnung,
  updateMahnungNotizen,
  setMahnungStatusPaid,
} from "../../../api/admin-mahnwesen-api";
import { formatToGermanPrice } from "../../../util/PriceUtil";
import { SnackBarContext } from "../../../context/snackbar-context";
import ConfirmationDialog from "./ConfirmationDialog";

const AdminMahnungDialog: React.FC<{
  isOpen: boolean;
  onCloseDialog: () => void;
  idMahnung: number;
}> = ({ isOpen, onCloseDialog, idMahnung }) => {
  const snackBarCtx = useContext(SnackBarContext);
  const dialogRef = useRef<HTMLDivElement>(null);

  const [mahnung, setMahnung] = useState<Mahnung>();
  const [isMahnstufe2, setIsMahnstufe2] = useState<boolean>(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [isInkasso, setIsInkasso] = useState<boolean>(false);
  const [originalNotizen, setOriginalNotizen] = useState<string>("");

  const [confirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState<boolean>(false);
  const [confirmationDialogText, setConfirmationDialogText] =
    useState<string>();
  const [pendingAction, setPendingAction] = useState<
    (() => Promise<void>) | null
  >(null);

  const openConfirmation = (text: string, action: () => Promise<void>) => {
    setConfirmationDialogText(text);
    setPendingAction(() => action); // store the function
    setIsConfirmationDialogOpen(true);
  };

  // when user clicks "Yes"
  const handleConfirmYes = async () => {
    if (pendingAction) {
      await pendingAction(); // run stored action
    }
    setIsConfirmationDialogOpen(false);
    setPendingAction(null);
  };

  // when user clicks "No"
  const handleConfirmNo = () => {
    setIsConfirmationDialogOpen(false);
    setPendingAction(null);
  };

  useEffect(() => {
    if (!isOpen && dialogRef.current) {
      dialogRef.current.setAttribute("inert", "true");
    } else if (isOpen && dialogRef.current) {
      dialogRef.current.removeAttribute("inert");
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchMahnung = async () => {
      const mahnung = await getMahnung(idMahnung);
      setMahnung(mahnung);
      setOriginalNotizen(mahnung.Notizen || "");
      const isCompleted =
        mahnung.Status === MahnungStatus.STOPPED ||
        mahnung.Status === MahnungStatus.PAID;
      const isInkasso = mahnung.Status === MahnungStatus.INKASSO;
      const isMahnstufe2 =
        mahnung && mahnung.Mahnstufe === MahnstufeEnum.MAHNSTUFE_2;
      setIsInkasso(isInkasso);
      setIsCompleted(isCompleted);
      setIsMahnstufe2(isMahnstufe2);
    };

    if (idMahnung) {
      fetchMahnung();
    }
  }, [isOpen, idMahnung]);

  const handleNotizenChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const updatedNotizen = event.target.value;
    setMahnung((prevMahnung) => ({
      ...prevMahnung!,
      Notizen: updatedNotizen,
    }));
  };

  const handleNotizenSave = async () => {
    if (mahnung.Notizen !== originalNotizen && mahnung.Notizen) {
      await updateMahnungNotizen(mahnung.ID_Mahnung, mahnung.Notizen);
      setOriginalNotizen(mahnung.Notizen);
    }
  };

  const groupByGebuhrType = (list: Mahngebuhr[], type: GebuhrTypes) => {
    return list.filter((g) => g.Gebuhrtype === type);
  };

  const gebuhrTypeLabels = {
    [GebuhrTypes.AUFNAHME]: "Aufnahmegebühr",
    [GebuhrTypes.BEITRAG]: "ausstehende Beiträge",
    [GebuhrTypes.RUCKLAST]: "Bankgebühren",
    [GebuhrTypes.MAHN]: "Mahngebühren",
  };

  return (
    <>
      <Dialog
        aria-hidden={!isOpen}
        open={isOpen}
        onClose={onCloseDialog}
        keepMounted
        sx={{
          "& .MuiDialog-paper": {
            width: "75vw",
            minWidth: "500px",
            maxWidth: "1000px",
            height: "auto",
            maxHeight: "90vh",
            borderRadius: "8px",
            border: "1px solid rgba(96, 119, 131, 0.50)",
            boxShadow: "0px 20px 20px 0px rgba(0, 0, 0, 0.25)",
            padding: "20px",
            "@media (max-width: 768px)": {
              width: "95vw",
              padding: "15px",
            },
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "30px",
          }}
        >
          <Box
            onClick={onCloseDialog}
            sx={{
              position: "absolute",
              top: "5px",
              right: "10px",
              cursor: "pointer",
            }}
          >
            <img
              alt="close"
              src={xGrey}
              style={{ width: "15px", height: "15px" }}
            />
          </Box>
          {mahnung && (
            <>
              <Box
                width="100%"
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 2,
                  "@media (max-width: 768px)": {
                    gridTemplateColumns: "1fr",
                  },
                }}
              >
                {/* Kunde Info Box */}
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    padding: 1.5,
                    borderRadius: 2,
                  }}
                >
                  <Typography fontWeight={600} fontSize={14} marginBottom={1}>
                    Kunde
                  </Typography>
                  <Typography fontSize={14}>
                    <b>ID Kunde:</b> {mahnung.ID_Kunde}
                  </Typography>
                  <Typography fontSize={14}>
                    <b>Name:</b> {mahnung.Vorname} {mahnung.Nachname}
                  </Typography>
                </Box>

                {/* Mahnung Info Box */}
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    padding: 1.5,
                    borderRadius: 2,
                  }}
                >
                  <Typography fontWeight={600} fontSize={14} marginBottom={1}>
                    Mahnung
                  </Typography>
                  <Typography fontSize={14}>
                    <b>Mahnstufe:</b> {mahnung.Mahnstufe ?? "N/A"}
                  </Typography>
                  <Typography fontSize={14}>
                    <b>Mahnfrist:</b>{" "}
                    {mahnung.Mahnfrist
                      ? new Date(mahnung.Mahnfrist).toLocaleDateString("de-DE")
                      : "-"}
                  </Typography>
                  <Typography fontSize={14}>
                    <b>Summe:</b> {formatToGermanPrice(mahnung.Summe)} €
                  </Typography>
                </Box>

                {/* Gebühren Boxes */}
                {(Object.values(GebuhrTypes) as GebuhrTypes[]).map((type) => {
                  const items = groupByGebuhrType(
                    mahnung.Mahngebuhren ?? [],
                    type
                  );
                  return (
                    <Box
                      key={type}
                      sx={{
                        border: "1px solid #ccc",
                        padding: 1.5,
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        fontWeight={600}
                        fontSize={14}
                        marginBottom={1}
                      >
                        {gebuhrTypeLabels[type]}
                      </Typography>

                      <Box
                        sx={{
                          maxHeight: "60px",
                          overflowY: "auto",
                        }}
                      >
                        {items.length === 0 ? (
                          <Typography fontSize={12} color="text.secondary">
                            Keine Einträge
                          </Typography>
                        ) : (
                          items.map((item, idx) => (
                            <Typography key={idx} fontSize={12}>
                              {new Date(item.Datum).toLocaleDateString("de-DE")}{" "}
                              — {formatToGermanPrice(item.Gebuhr)} €
                            </Typography>
                          ))
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <Box
                sx={{
                  width: "100%",
                  marginTop: "20px",
                  border: "1px solid #ccc",
                  borderRadius: "8px",
                  minHeight: "100px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  fontWeight={600}
                  fontSize={14}
                  marginBottom={1}
                  sx={{
                    width: "95%",
                    textAlign: "left",
                  }}
                >
                  Notizen
                </Typography>

                <TextareaAutosize
                  value={mahnung.Notizen || ""}
                  onChange={handleNotizenChange}
                  onBlur={handleNotizenSave}
                  minRows={3}
                  style={{
                    width: "95%",
                    padding: "8px",
                    fontSize: "13px",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                    resize: "none",
                  }}
                />
              </Box>
            </>
          )}

          {/* Buttons */}
          {!isCompleted && (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
                padding: "20px",
                gap: "10px",
              }}
            >
              <Box>
                <Button
                  sx={{
                    display: "flex",
                    width: "200px",
                    height: "50px",
                    padding: "13px 40px",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "10px",
                    flexShrink: 0,
                    borderRadius: "4px",
                    border: !isCompleted
                      ? "2px solid #DE3730"
                      : "2px solid var(--grey)",
                    background: "#FFF",
                    "&:hover": {
                      backgroundColor: "#FFF",
                      filter: "brightness(90%)",
                      transition: "background-color 0.3s, filter 0.3s",
                    },
                  }}
                  onClick={async () => {
                    if (!isCompleted) {
                      openConfirmation(
                        "Möchten Sie die Mahnung wirklich einstellen?",
                        async () => {
                          await resetMahnung(mahnung.ID_Mahnung);
                          onCloseDialog();
                        }
                      );
                    }
                  }}
                >
                  <Typography
                    color={!isCompleted ? "#DE3730" : "var(--grey)"}
                    fontWeight={900}
                  >
                    <strong>Einstellen</strong>
                  </Typography>
                </Button>
              </Box>

              <Box sx={{ display: "flex", gap: "10px" }}>
                <AdminManagementButton
                  variant="contained"
                  text={"Zahlung erhalten"}
                  color={"var(--orange)"}
                  onClick={async () => {
                    if (!isCompleted && !isInkasso) {
                      openConfirmation(
                        "Zahlung wirklich als erhalten markieren?",
                        async () => {
                          await setMahnungStatusPaid(mahnung.ID_Mahnung);
                          onCloseDialog();
                        }
                      );
                    }
                  }}
                />
                <AdminManagementButton
                  variant="contained"
                  text={
                    isMahnstufe2 ? "An Inkasso übergeben" : "Mahnstufe erhöhen"
                  }
                  color={
                    !isCompleted && !isInkasso ? "var(--green)" : "var(--grey)"
                  }
                  onClick={async () => {
                    if (!isCompleted && !isInkasso) {
                      openConfirmation(
                        isMahnstufe2
                          ? "Möchten Sie die Mahnung wirklich an Inkasso übergeben?"
                          : "Möchten Sie die Mahnstufe wirklich erhöhen?",
                        async () => {
                          try {
                            await incrementMahnung(mahnung.ID_Mahnung);
                            onCloseDialog();
                            snackBarCtx.openSnackbar(
                              isMahnstufe2
                                ? "Mahnung wurde erfolgreich an Inkasso übergeben."
                                : "Mahnung wurde erfolgreich erhöht.",
                              "success"
                            );
                          } catch (err: any) {
                            console.error(err);
                            const message =
                              err?.response?.data?.message ||
                              err?.message ||
                              "Unbekannter Fehler beim Erhöhen der Mahnung.";
                            snackBarCtx.openSnackbar(message, "error");
                          }
                        }
                      );
                    }
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>

      <ConfirmationDialog
        isOpen={confirmationDialogOpen}
        text={confirmationDialogText}
        noBtnClick={handleConfirmNo}
        yesBtnClick={handleConfirmYes}
      />
    </>
  );
};

export default AdminMahnungDialog;
